import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { selectToken } from 'containers/authentication/authenticationSlice'
import api from 'containers/api'
import { useGoogleLogin } from '@react-oauth/google'
import useMicrosoft from 'containers/authentication/useMicrosoft'
import useStorage from 'containers/useStorage'

interface Provider {
  name: string
  icon: string
  connected: boolean
  canDisconnect: boolean
}

const LoginProvidersTab = () => {
  const token = useSelector(selectToken)
  const location = useLocation()
  const storage = useStorage()
  const { getAuthUri } = useMicrosoft()
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)
  const [providers, setProviders] = useState<Provider[]>([
    {
      name: 'Email & Password',
      icon: 'email',
      connected: false,
      canDisconnect: true,
    },
    {
      name: 'Google',
      icon: 'https://www.google.com/favicon.ico',
      connected: false,
      canDisconnect: true,
    },
    {
      name: 'Microsoft',
      icon: 'https://www.microsoft.com/favicon.ico',
      connected: false,
      canDisconnect: true,
    },
  ])
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    fetchProviders()
  }, [])

  const fetchProviders = async () => {
    try {
      setLoading(true)
      const data = await api.getProviderStatus({ token })

      setProviders([
        {
          name: 'Email & Password',
          icon: 'email',
          connected: data.email_password,
          canDisconnect: true,
        },
        {
          name: 'Google',
          icon: 'https://www.google.com/favicon.ico',
          connected: data.google,
          canDisconnect: true,
        },
        {
          name: 'Microsoft',
          icon: 'https://www.microsoft.com/favicon.ico',
          connected: data.microsoft,
          canDisconnect: true,
        },
      ])
    } catch (err) {
      console.error('Error fetching providers:', err)
      setError('Failed to load provider status')
    } finally {
      setLoading(false)
    }
  }

  const connectedCount = providers.filter((p) => p.connected).length

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        localStorage.setItem('oauth_intent', 'connect_provider')
        localStorage.setItem('oauth_provider', 'google')

        const success = await api.connectProvider({
          googleAccessToken: tokenResponse.access_token,
          token
        })

        if (success) {
          localStorage.removeItem('oauth_intent')
          localStorage.removeItem('oauth_provider')
          await fetchProviders()
          setError('')
        }
      } catch (err: any) {
        console.error('Failed to connect Google:', err)
        setError('Failed to connect Google account')
        localStorage.removeItem('oauth_intent')
        localStorage.removeItem('oauth_provider')
      }
    },
    onError: () => {
      setError('Failed to connect Google account')
      localStorage.removeItem('oauth_intent')
      localStorage.removeItem('oauth_provider')
    },
  })

  const handleConnect = async (providerName: string) => {
    const providerKey = providerName.toLowerCase()

    if (providerKey === 'microsoft') {
      // Store current URL AND connection intent
      storage.setUrlWhenLoggedIn({ url: location.pathname })
      localStorage.setItem('oauth_intent', 'connect_provider')
      localStorage.setItem('oauth_provider', 'microsoft')

      // Use the proper Microsoft OAuth flow
      const response = await getAuthUri(null)
      if (response) {
        window.location.href = response.auth_uri
      } else {
        setError('Failed to initiate Microsoft authentication')
        // Clear intent flags on error
        localStorage.removeItem('oauth_intent')
        localStorage.removeItem('oauth_provider')
      }
    } else if (providerKey === 'google') {
      googleLogin()
    } else {
      setError('Provider not supported for connection')
    }
  }

  const handleDisconnect = async (providerName: string) => {
    if (connectedCount <= 1) {
      setError('You must have at least one login method connected')
      return
    }

    const confirmed = window.confirm(`Disconnect ${providerName}?`)
    if (!confirmed) return

    try {
      setError('')
      const providerKey = providerName.toLowerCase().replace(' & ', '')
      await api.disconnectProvider({ provider: providerKey, token })

      // Refresh provider list
      await fetchProviders()
    } catch (err: any) {
      console.error('Error disconnecting provider:', err)
      if (err?.response?.data?.type === 'last_provider') {
        setError('Cannot disconnect your only login method')
      } else {
        setError('Failed to disconnect provider')
      }
    }
  }

  return (
    <div style={{ marginTop: '1rem', width: '100%', maxWidth: '700px', padding: '0 1rem', boxSizing: 'border-box' }}>
      <div
        style={{
          padding: '1rem',
          backgroundColor: '#fff3cd',
          border: '1px solid #ffeaa7',
          borderRadius: '4px',
          marginBottom: '2rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <span className="material-icons-outlined" style={{ color: '#856404' }}>
            info
          </span>
          <strong style={{ color: '#856404' }}>Security Tip</strong>
        </div>
        <p style={{ margin: '0.5rem 0 0 0', color: '#856404', fontSize: '0.9rem' }}>
          Keep at least one login method active at all times to maintain access to your
          account.
        </p>
      </div>

      {error && (
        <div style={{
          padding: '1rem',
          backgroundColor: '#f8d7da',
          border: '1px solid #f5c2c7',
          borderRadius: '4px',
          marginBottom: '1rem',
          color: '#842029'
        }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}>Loading providers...</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {providers.map((provider) => (
          <div
            key={provider.name}
            style={{
              padding: '1.5rem',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              display: 'flex',
              flexWrap: isMobile ? 'wrap' : 'nowrap',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: isMobile ? '1rem' : '0',
              backgroundColor: '#fff',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flex: '1 1 auto', minWidth: '200px' }}>
              {provider.icon.startsWith('http') ? (
                <img
                  src={provider.icon}
                  alt={provider.name}
                  style={{ width: '32px', height: '32px', flexShrink: 0 }}
                />
              ) : (
                <span
                  className="material-icons-outlined"
                  style={{ fontSize: '2rem', color: '#495057', flexShrink: 0 }}
                >
                  {provider.icon}
                </span>
              )}
              <div style={{ minWidth: 0 }}>
                <h4 style={{ margin: '0 0 0.25rem 0', wordBreak: 'break-word' }}>{provider.name}</h4>
                <p style={{ margin: 0, fontSize: '0.85rem', color: '#6c757d' }}>
                  {provider.connected ? (
                    <span style={{ color: '#28a745', fontWeight: 500 }}>
                      <span className="material-icons-outlined" style={{ fontSize: '1rem', verticalAlign: 'middle' }}>
                        check_circle
                      </span>{' '}
                      Connected
                    </span>
                  ) : (
                    <span style={{ color: '#999' }}>Not connected</span>
                  )}
                </p>
              </div>
            </div>

            <div className="provider-button-container" style={{ flex: '0 0 auto'}}>
              {provider.connected ? (
                <button
                  type="button"
                  className="submit-button"
                  style={{
                    backgroundColor: '#dc3545',
                    padding: '0.5rem 1.5rem',
                    fontSize: '0.9rem',
                    minWidth: '120px',
                    whiteSpace: 'nowrap',
                    width: "100%",
                  }}
                  onClick={() => handleDisconnect(provider.name)}
                  disabled={!provider.canDisconnect || connectedCount <= 1}
                >
                  Disconnect
                </button>
              ) : (
                <button
                  type="button"
                  className="submit-button"
                  style={{
                    padding: '0.5rem 1.5rem',
                    fontSize: '0.9rem',
                    minWidth: '100px',
                    whiteSpace: 'nowrap',
                  }}
                  onClick={() => handleConnect(provider.name)}
                >
                  Connect
                </button>
              )}
            </div>
          </div>
        ))}
        </div>
      )}

      <div
        style={{
          marginTop: '2rem',
          padding: '1rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
        }}
      >
        <h4 style={{ marginTop: 0 }}>About Login Providers</h4>
        <p style={{ fontSize: '0.9rem', color: '#6c757d', lineHeight: '1.6' }}>
          You can use multiple login methods to access your account. This provides
          flexibility and ensures you can always access your account even if one method
          becomes unavailable.
        </p>
      </div>
    </div>
  )
}

export default LoginProvidersTab
